import { NEIGONG, STATUSES } from '@nine/shared';
import { STATUS_TINT } from '../emblems';
import { bell, drop, eye, fan, figure, flames, heart, lotus, peak, snowflake, sprite, star } from '../kit';
import { neigongColor, neigongEmblem } from '../neigong/kind';
import { C } from '../pix';
import { defineIconKind, type Draw, registerIcons } from '../store';

/**
 * Statuses, buffs and debuffs: 16×16 tiles for the status rows over heads and frames (doubled for
 * 32). Debuffs wear a seal-red border. The lasting gifts of neigong (np-…) show their book's
 * emblem on a tile of its colour, with a gold stud for "kept while the book is active".
 *
 * Besides the game's statuses there are a few states the HUD shows as status icons: stunned,
 * meditating, the training-spot bonus, and the group dance's afterglow (พลังประสาน).
 */

export const STATUS_EXTRAS: Readonly<Record<string, { name: string; tint: string; debuff?: boolean }>> = {
  stun: { name: 'มึนงง', tint: '#f0d040', debuff: true },
  meditation: { name: 'นั่งสมาธิ', tint: '#f0d696' },
  'training-spot': { name: 'จุดฝึกวิชา', tint: '#6ac0e0' },
  'group-dance': { name: 'พลังประสาน', tint: '#ff9ad0' },
};

/** The neigong whose passive a status is. */
const bookOf = (statusId: string) => Object.values(NEIGONG).find((n) => n.passive.status === statusId);

defineIconKind('status', {
  label: 'สถานะ บัพ และดีบัพ',
  ids: () => [...Object.keys(STATUSES), ...Object.keys(STATUS_EXTRAS)],
  name: (id) => STATUSES[id]?.name ?? STATUS_EXTRAS[id]?.name ?? id,
  tint: (id) => {
    const book = bookOf(id);
    if (book) return neigongColor(book);
    return STATUS_TINT[id] ?? STATUS_EXTRAS[id]?.tint ?? C.gold;
  },
  plate: 'square',
  native: 16,
  marker: (id) => (STATUSES[id]?.kind === 'debuff' || STATUS_EXTRAS[id]?.debuff ? 'debuff' : 'none'),
  fallback: (id) => {
    const book = bookOf(id);
    if (book)
      return {
        native: 16,
        draw: (p) => {
          neigongEmblem(p, book.id, 8, 8.5, 4.6);
          p.px(13, 2, C.gold).px(12, 2, C.goldLight);
        },
      };
    return { native: 16, draw: (p) => p.ell(8, 8, 3, 3, C.paper) };
  },
});

const at16 = (draw: Draw) => ({ native: 16 as const, draw });

registerIcons('status', {
  'snow-daze': at16((p) => {
    snowflake(p, 8, 9, 4.5, C.white);
    star(p, 3, 3, 1, '#ffe07a');
    star(p, 13, 4, 1, '#ffe07a');
  }),
  chill: at16((p) => {
    snowflake(p, 9, 7, 4, C.ice, false);
    p.rect(2, 12, 7, 1, C.white).rect(5, 10, 6, 1, C.white);
  }),
  'frost-trap': at16((p) => {
    p.rect(3, 4, 10, 9, C.ice);
    p.rect(4, 5, 3, 1, C.white).rect(4, 6, 1, 2, C.white);
    p.path(
      [
        [10, 4],
        [8, 8],
        [10, 10],
        [8, 12],
      ],
      C.iceDark,
    );
  }),
  slow: at16((p) => {
    // A snail.
    p.rect(2, 11, 11, 2, '#d8d0a0');
    p.rect(2, 8, 2, 3, '#d8d0a0');
    p.px(2, 6, '#d8d0a0').px(4, 6, '#d8d0a0').px(2, 7, '#d8d0a0').px(4, 7, '#d8d0a0');
    p.ell(9, 8, 4, 4, '#c88a50');
    p.ell(9, 8, 2, 2, '#8a5a30');
    p.px(9, 8, '#e8b880');
  }),
  haste: at16((p) => {
    for (const x of [3, 8])
      sprite(p, x, 4, ['ss...', '.ss..', '..ss.', '...ss', '..ss.', '.ss..', 'ss...'], {
        s: x === 8 ? C.white : C.qi,
      });
    p.rect(1, 13, 6, 1, C.qi);
  }),
  'snow-pride': at16((p) => {
    p.rect(7, 5, 2, 9, '#eef6ff');
    p.rect(4, 5, 8, 1, '#7ad0f0');
    p.rect(7, 2, 2, 3, C.woodDark);
    snowflake(p, 12, 10, 2, C.white, false);
  }),
  'iron-bell': at16((p) => bell(p, 8, 4, 9, C.gold, { w: 9 })),
  bleed: at16((p) => {
    drop(p, 7, 10, 3.6, C.blood);
    drop(p, 12, 6, 1.8, C.blood);
  }),
  'war-cry': at16((p) => {
    p.line(4, 2, 4, 14, C.wood);
    p.poly(
      [
        [5, 3],
        [13, 4],
        [11, 6],
        [13, 8],
        [5, 9],
      ],
      C.seal,
    );
    p.px(8, 6, C.gold);
  }),
  poison: at16((p) => {
    drop(p, 7, 10, 3.6, C.poison);
    p.px(6, 10, C.poisonDark).px(8, 10, C.poisonDark);
    p.ell(12, 5, 1.5, 1.5, C.poison);
    p.px(3, 4, C.poison);
  }),
  charm: at16((p) => {
    heart(p, 8, 8, 5, '#ff7ac8');
    p.px(6, 7, C.white).px(10, 7, C.white);
  }),
  silence: at16((p) => {
    // A padlock: the acupoints are sealed.
    p.ring(8, 7, 3, 3, C.steelMid, Math.PI, Math.PI * 2);
    p.rect(5, 7, 1, 1, C.steelMid).rect(11, 7, 1, 1, C.steelMid);
    p.rect(4, 8, 9, 6, '#c8a040');
    p.rect(8, 10, 1, 2, C.ink);
  }),
  ward: at16((p) => fan(p, 8, 13, 9, C.paper, C.wood)),
  focus: at16((p) => {
    eye(p, 8, 8, 12, '#3a8ac8');
    star(p, 13, 3, 1, C.white);
  }),
  'blood-frenzy': at16((p) => {
    flames(p, 2, 14, 12, 11, C.blood, '#ff7a6a');
    p.rect(6, 9, 4, 2, '#ffe0a0');
    p.px(7, 9, C.ink).px(8, 10, C.ink);
  }),
  'meal-might': at16((p) => {
    p.line(2, 12, 14, 4, C.wood);
    p.ell(8, 8, 4.5, 2.4, '#e8903a');
    p.poly(
      [
        [3, 6],
        [5, 8],
        [3, 10],
      ],
      '#e8903a',
    );
    p.px(11, 7, C.ink);
  }),
  'meal-guard': at16((p) => {
    p.poly(
      [
        [2, 8],
        [14, 8],
        [12, 13],
        [4, 13],
      ],
      '#e8eef8',
    );
    p.rect(3, 8, 10, 1, '#7ac05a');
    p.rect(5, 13, 6, 1, '#a8b8d0');
    for (const x of [5, 8, 11]) p.line(x, 6, x + 1, 3, C.white);
  }),
  'meal-feast': at16((p) => {
    p.ell(8, 11, 6.5, 2.5, C.white);
    p.ell(8, 9, 4, 2, '#e8703a');
    p.poly(
      [
        [3, 7],
        [5, 9],
        [3, 11],
      ],
      '#e8703a',
    );
    p.px(10, 8, C.ink);
    p.line(6, 5, 7, 2, C.white).line(10, 5, 11, 2, C.white);
  }),
  sunder: at16((p) => {
    sprite(
      p,
      3,
      3,
      [
        'ssssssssss',
        's.s.s.s.ss',
        'ssssssssss',
        'ss.s.s.s.s',
        'ssssssssss',
        's.s.s.s.ss',
        'ssssssssss',
        '.ssssssss.',
        '..ssssss..',
      ],
      { s: '#c89060' },
    );
    p.path(
      [
        [8, 3],
        [6, 6],
        [9, 8],
        [7, 11],
      ],
      C.ink,
    );
  }),
  shackle: at16((p) => {
    // A cangue: a wooden board locked round the neck.
    p.rect(2, 5, 12, 7, '#a8784a');
    p.rect(2, 5, 12, 1, '#c8986a');
    p.ell(8, 8.5, 2, 2, C.ink);
    p.rect(3, 12, 1, 3, C.steelMid).rect(12, 12, 1, 3, C.steelMid);
  }),
  trance: at16((p) => {
    figure(p, 5, 4, C.white, 'leap', { head: C.skin });
    p.ring(8, 8, 6, 3, '#ff9ad0', 0, Math.PI * 1.6);
  }),
  stun: at16((p) => {
    p.ring(8, 7, 5, 2, '#f0d040');
    star(p, 3, 7, 1, C.white);
    star(p, 13, 7, 1, C.white);
    star(p, 8, 10, 1, '#ffe07a');
    p.ell(8, 12.5, 3, 1.5, C.skin);
  }),
  meditation: at16((p) => {
    lotus(p, 8, 13, 12, '#f0d696', C.paper);
    p.ell(8, 4, 1.6, 1.6, C.skin);
    p.rect(6, 6, 5, 4, '#f0d696');
    p.rect(4, 9, 9, 2, '#f0d696');
  }),
  'training-spot': at16((p) => {
    peak(p, 8, 14, 13, 10, '#8aa0b8', C.paper);
    p.line(11, 2, 11, 7, C.wood);
    p.poly(
      [
        [12, 2],
        [15, 3],
        [12, 5],
      ],
      C.seal,
    );
  }),
  'group-dance': at16((p) => {
    for (const x of [2, 7, 12]) {
      p.ell(x + 1, 6, 1.4, 1.4, C.skin);
      p.rect(x, 8, 3, 4, x === 7 ? '#ff9ad0' : C.white);
      p.px(x, 13, C.white).px(x + 2, 13, C.white);
    }
    p.rect(4, 9, 4, 1, C.white).rect(9, 9, 4, 1, C.white);
    p.rect(12, 2, 1, 3, C.white).px(11, 4, C.white);
  }),
});
